import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent, Slide, Button, DialogActions, useMediaQuery } from '@mui/material';
import { Info as InfoIcon, Close as CloseIcon } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { DomainConfig } from '../api/domains';
import { getDomainMeta } from '../utils/workItemStyling';

const ROADMAP_DOMAIN_KEYS = ['job_hunt', 'business', 'finance', 'learning', 'parenthood', 'personal', 'health', 'general'] as const;
const ROADMAP_DOMAIN_KEY_SET = new Set<string>(ROADMAP_DOMAIN_KEYS);

function normalizeDomainKey(value?: string | null) {
  const normalized = (value || '').toLowerCase().trim();
  if (normalized === 'job search' || normalized === 'application' || normalized === 'applied') return 'job_hunt';
  if (normalized === 'work') return 'business';
  return normalized;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ColorLegendProps {
  open: boolean;
  onClose: () => void;
  domains?: DomainConfig[];
}

/**
 * ColorLegend Component
 * 
 * Displays a Dialog explaining the color coding system.
 */
const ColorLegend: React.FC<ColorLegendProps> = ({ open, onClose, domains }) => {
  // Below 1024 px the palette button is not on Today; the legend lives in the menu.
  const isDesktop = useMediaQuery('(min-width: 1024px)', { noSsr: true });
  const domainColors = useMemo(() => {
    const systemDefaults = (domains ?? [])
      .filter((d) => (d.key || '').trim().length > 0 && d.is_active !== false && d.is_system_default === true)
      .map((d) => ({ ...d, normalizedKey: normalizeDomainKey(d.key) }))
      .filter((d) => ROADMAP_DOMAIN_KEY_SET.has(d.normalizedKey));

    const keysToRender = systemDefaults.length > 0
      ? ROADMAP_DOMAIN_KEYS.filter((key) => systemDefaults.some((d) => d.normalizedKey === key))
      : [...ROADMAP_DOMAIN_KEYS];

    return keysToRender.map((key, index) => {
      const base = getDomainMeta(key);
      const configured = systemDefaults.find((d) => d.normalizedKey === key);
      return {
        key,
        label: (configured?.label || base.label || key).trim(),
        // Use the shared styling palette so the legend always reflects available UI colors.
        color: base.color,
        icon: (configured?.icon || base.icon || '•').trim() || base.icon,
        sortOrder: index,
      };
    });
  }, [domains]);

  const urgencyColors = [
    { urgency: 'Overdue', color: '#ef4444', description: 'Past due date' },
    { urgency: 'Due Today', color: '#f97316', description: 'Due within 24 hours' },
    { urgency: 'This Week', color: '#eab308', description: 'Due this week' },
    { urgency: 'Later', color: '#22c55e', description: 'Due in the future' },
    { urgency: 'No Due Date', color: '#6b7280', description: 'Not scheduled' },
  ];

  const exampleDomain = domainColors.find((d) => d.key === 'job_hunt')
    ?? domainColors.find((d) => d.key !== 'general')
    ?? domainColors[0];
  const exampleUrgency = urgencyColors.find((u) => u.urgency === 'Due Today') ?? urgencyColors[0];

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-bg-secondary)',
          backgroundImage: 'none',
          border: '1px solid var(--color-border)',
          borderRadius: 4,
          color: 'var(--color-text-primary)',
          maxWidth: 560,
          width: 'calc(100% - 32px)',
          boxShadow: 'var(--shadow-lg)',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid var(--color-border)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: 'var(--color-accent-indigo)' }} />
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1.1 }}>
              Colors = Domain Context, Not Urgency
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
              Left border shows domain context. Outer border/glow shows urgency.
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'var(--color-text-muted)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ color: 'var(--color-text-muted)', fontWeight: 900, letterSpacing: '0.1em' }}>
            Anatomy Of A Card (Example)
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 3,
              bgcolor: 'var(--color-bg-card)',
              border: `2px solid ${exampleUrgency.color}`,
              borderLeft: `6px solid ${exampleDomain.color}`,
            }}
          >
            <Typography sx={{ fontWeight: 900, letterSpacing: '-0.01em' }}>
              Reply to recruiter and confirm interview time
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.25,
                  borderRadius: 99,
                  bgcolor: 'var(--color-bg-tertiary)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '10px',
                  fontWeight: 900,
                  maxWidth: '100%',
                }}
              >
                <span aria-hidden>{exampleDomain.icon}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exampleDomain.label}</span>
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', fontWeight: 700 }}>
                {exampleUrgency.urgency}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 2, display: 'grid', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: exampleDomain.color, borderRadius: 1 }} />
              <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                Left border = <strong>domain</strong> ({exampleDomain.label})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 14, height: 14, border: `2px solid ${exampleUrgency.color}`, borderRadius: 1 }} />
              <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                Outer border/glow = <strong>urgency</strong> ({exampleUrgency.urgency})
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ color: 'var(--color-text-muted)', fontWeight: 900, letterSpacing: '0.1em' }}>
            Domain Colors (Left Border)
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--color-text-muted)' }}>
            Showing product-default domains so the palette stays consistent.
          </Typography>
          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {domainColors.map(({ key, label, color, icon }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 4, height: 16, bgcolor: color, borderRadius: 1 }} />
                <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)', fontWeight: 700 }}>
                  <span style={{ marginRight: 8 }} aria-hidden>{icon}</span>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography variant="overline" sx={{ color: 'var(--color-text-muted)', fontWeight: 900, letterSpacing: '0.1em' }}>
            Urgency (Outer Border/Glow)
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {urgencyColors.map(({ urgency, color, description }) => (
              <Box key={urgency} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 14, height: 14, border: `2px solid ${color}`, borderRadius: '4px', boxShadow: urgency === 'Overdue' ? `0 0 10px ${color}44` : 'none' }} />
                <Box>
                  <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', fontWeight: 800 }}>{urgency}</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>{description}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
          {isDesktop ? 'Tip: Open this anytime from the 🎨 button on Today.' : 'Tip: Open this anytime from the menu.'}
        </Typography>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 900,
            borderRadius: 2,
            bgcolor: 'var(--color-accent-indigo)',
            color: '#fff',
            '&:hover': { bgcolor: 'var(--color-accent-indigo)' },
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColorLegend;
